import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { Card, Container } from 'reactstrap';
import { Button, useMobile, addToast } from '@octano/global-ui';

import TeacherSurveyLoader from './parts/TeacherSurveyLoader';
import Survey from '../../components/survey/Survey';
import TopBar from '../../components/topBar/TopBar';
import { SectionDetailBox } from '../../components/sectionDetailBox/SectionDetailBox';
import { ModalConfirmation } from '../../components/modals/Confirmation';
import { submitSurvey } from './api';
import { CreateTeacherSurveyDto } from './types';

export default function TeacherSurvey() {
  const { t } = useTranslation();
  const history = useHistory();
  const isMobile = useMobile();
  const [openModal, setOpenModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [surveyData, setSurveyData] = useState<CreateTeacherSurveyDto>({
    responses: {},
    comments: null,
  });
  const { id: sectionId, teacherId } = useParams<{
    id: string;
    teacherId: string;
  }>();

  const handleSubmit = async (data: any) => {
    setSurveyData(data);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    if (loading) {
      return;
    }
    setOpenModal(false);
  };

  const handleConfirmSubmit = async () => {
    setLoading(true);
    if (surveyData) {
      const res = await submitSurvey(sectionId, teacherId, surveyData);
      if (res.error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('myCourses.surveys.teacherSurvey.modal.failure'),
        });
        setLoading(false);
      } else {
        addToast({
          icon: 'success',
          color: 'success',
          text: t('myCourses.surveys.teacherSurvey.modal.success'),
        });
        setLoading(false);
      }
      setOpenModal(false);

      history.push('/student-management/courses');
    }
  };

  return (
    <TeacherSurveyLoader>
      {({ surveyData }) => (
        <>
          <TopBar title={t(`myCourses.surveys.teacherSurvey.title`)} />
          <Container fluid>
            <Card className={`${isMobile ? '' : 'py-4 px-4'}`}>
              <div
                className="d-flex flex-wrap justify-content-between mb-5"
                style={{ gap: isMobile ? 12 : 0 }}
              >
                {!isMobile && (
                  <Button
                    text={t(`common.btnBack`)}
                    icon="back"
                    size="sm"
                    outlined
                    rounded
                    style={{ width: 'fit-content', height: 'fit-content' }}
                    onClick={() => history.push('/student-management/courses')}
                    className={`${isMobile ? 'mt-4 ml-2' : ''}`}
                  />
                )}

                {!isMobile && (
                  <SectionDetailBox
                    course={surveyData.section?.course?.name}
                    shortening={surveyData.section?.course?.shortening}
                    section={surveyData.section?.name}
                    professors={surveyData.teacher?.account?.fullName}
                  />
                )}
              </div>

              <div className={`${isMobile ? '' : 'px-5'}`}>
                {!isMobile && (
                  <div className="h5 mb-4">
                    {t('myCourses.surveys.teacherSurvey.description')}
                  </div>
                )}

                <Survey survey={surveyData.survey} onSubmit={handleSubmit} />
              </div>
            </Card>
            <ModalConfirmation
              open={openModal}
              title={t('myCourses.surveys.teacherSurvey.modal.title')}
              subtitle={t('myCourses.surveys.teacherSurvey.modal.subtitle')}
              onConfirm={handleConfirmSubmit}
              onClose={handleCloseModal}
              loading={loading}
            />
          </Container>
        </>
      )}
    </TeacherSurveyLoader>
  );
}
